import { HDate, HebrewCalendar, Molad, flags as F } from '@hebcal/core';

/**
 * Compute liturgy & nusach flags.
 *
 * @param {import('@hebcal/core').HDate} hdate
 * @param {object} calData - from calendarProvider
 * @param {boolean} isIsrael
 * @param {string} edah
 * @returns {object}
 */
export function computeLiturgy(hdate, calData, isIsrael, edah) {
  const m = hdate.getMonth();
  const d = hdate.getDate();

  // ═══ Musaf ═══
  const musafApplicable = calData.isShabbat || calData.isRoshChodesh ||
    calData.isYomTov || calData.isCholHamoed;

  // ═══ Yizkor ═══
  const yizkor = computeYizkor(hdate, isIsrael);

  // ═══ Birkat Kohanim ═══
  let birkatKohanim;
  if (isIsrael) {
    birkatKohanim = 'daily';
  } else if (edah === 'ashkenaz') {
    birkatKohanim = 'yomTov';
  } else {
    birkatKohanim = 'shabbatAndYomTov';
  }

  // ═══ Kedusha variant ═══
  let kedushaVariant = 'weekday';
  if (calData.isShabbat) kedushaVariant = 'shabbat';
  if (calData.isYomTov) kedushaVariant = 'yomTov';

  // ═══ Sifrei Torah ═══
  const sifreiTorah = computeSifreiTorah(calData);

  // ═══ Kiddush Levana ═══
  const kiddushLevana = computeKiddushLevana(hdate, edah);

  return {
    musafApplicable,
    yizkor,
    birkatKohanim,
    kedushaVariant,
    sifreiTorah,
    kiddushLevana,
  };
}

/**
 * Yizkor dates.
 * IL: 7th Pesach (21 Nisan), 1st Shavuot (6 Sivan), YK (10 Tishrei), Shmini Atzeret (22 Tishrei)
 * Diaspora: 8th Pesach (22 Nisan), 2nd Shavuot (7 Sivan), YK (10 Tishrei), Shmini Atzeret (22 Tishrei)
 */
function computeYizkor(hdate, isIsrael) {
  const m = hdate.getMonth();
  const d = hdate.getDate();

  if (m === 7 && d === 10) return true; // YK — same everywhere
  if (m === 7 && d === 22) return true; // Shmini Atzeret — same everywhere

  if (isIsrael) {
    if (m === 1 && d === 21) return true; // 7th Pesach
    if (m === 3 && d === 6) return true;  // Shavuot
  } else {
    if (m === 1 && d === 22) return true; // 8th Pesach
    if (m === 3 && d === 7) return true;  // 2nd Shavuot
  }

  return false;
}

/**
 * Count Torah scrolls needed.
 * 1: regular weekday/Shabbat
 * 2: RC on weekday, Chanukah on weekday, special Shabbat with maftir
 * 3: Shabbat + RC + Chanukah (e.g., Shabbat RC Tevet during Chanukah)
 */
function computeSifreiTorah(calData) {
  let count = 1;

  if (calData.isShabbat) count = 1; // Base: 1 Torah scroll

  // Special Shabbat with different maftir → 2nd scroll
  if (calData.specialShabbat || calData.isRoshChodesh || calData.isChanukah ||
      calData.isYomTov || calData.isCholHamoed) {
    count = 2;
  }

  // Triple: Shabbat + RC + Chanukah
  if (calData.isShabbat && calData.isRoshChodesh && calData.isChanukah) {
    count = 3;
  }

  // Shabbat + RC + special Shabbat (e.g., Shabbat Shekalim on RC)
  if (calData.isShabbat && calData.isRoshChodesh && calData.specialShabbat) {
    count = 3;
  }

  return count;
}

/**
 * Kiddush Levana eligibility — precise calculation based on molad.
 *
 * The blessing on the new moon can be recited from 3 days after the molad
 * (Ashkenazi custom; some Sephardic authorities wait 7 days) until the
 * middle of the month (approximately 14 days, 18 hours, 22 minutes after
 * the molad — half the lunar cycle of 29d 12h 44m 3.33s).
 *
 * For practical purposes we use 15 days after molad as the cutoff.
 *
 * @param {HDate} hdate
 * @param {string} [edah='ashkenaz']
 * @returns {object}
 */
function computeKiddushLevana(hdate, edah = 'ashkenaz') {
  const year = hdate.getFullYear();
  const month = hdate.getMonth();

  try {
    const molad = new Molad(year, month);

    // Calculate molad as fractional day from epoch
    // Molad gives us day-of-week, hour, minutes, chalakim
    // We need the absolute date of the molad relative to the Hebrew date system
    // The molad occurs near the 1st of the month by definition
    const firstOfMonth = new HDate(1, month, year);
    const moladAbs = firstOfMonth.abs(); // Approximate: molad is close to 1st of month

    // Earliest: 3 days after molad (Ashkenaz) or 7 days (Sephardic/Mizrahi)
    const earliestDays = (edah === 'sephardic' || edah === 'mizrahi') ? 7 : 3;
    const earliestAbs = moladAbs + earliestDays;
    const latestAbs = moladAbs + 15; // ~half lunar month

    const hdateAbs = hdate.abs();
    const eligible = hdateAbs >= earliestAbs && hdateAbs <= latestAbs;

    // Convert to Hebrew dates for display
    const earliestHDate = new HDate(earliestAbs);
    const latestHDate = new HDate(latestAbs);

    return {
      eligible,
      earliestDate: earliestHDate.renderGematriya(false),
      latestDate: latestHDate.renderGematriya(false),
      earliestDay: earliestHDate.getDate(),
      latestDay: latestHDate.getDate(),
    };
  } catch (err) {
    // Fallback to simple approximation
    const d = hdate.getDate();
    const earliest = (edah === 'sephardic' || edah === 'mizrahi') ? 7 : 3;
    return {
      eligible: d >= earliest && d <= 15,
      earliestDate: null,
      latestDate: null,
      earliestDay: earliest,
      latestDay: 15,
    };
  }
}
